import { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Container, Paper, Button } from "@mantine/core";
import { TextInput, Select, MultiSelect } from "react-hook-form-mantine";

import { AuthContext } from "../../services/context";
import { getCurrentWorkspace } from "../../services/userConfig";
import { useGetTags } from "../../api/tag";
import {
  useCreateSpecialInvitation,
  useUpdateSpecialInvitation,
} from "../../api/announcements";
import { useGetEvent } from "../../api/event";
import ImageUpload from "../ImageUpload";
import LocalizedInput from "../common/LocalizedInput";
import { extractDirtyData } from "../../utils/dirtyData";
import { web_specialInvitationSchema } from "@congrs/schemas";
import resolver from "@congrs/resolver";
import {
  makeI18nDefaultValue,
  mergeI18nWithDefaults,
} from "../../utils/i18nUtils";

function SpecialInvitationForm({ data = null, tags = null }) {
  const isEditMode = Boolean(data);

  const { user } = useContext(AuthContext);
  const { eventId } = getCurrentWorkspace(user);
  const currentEventId = data?.eventId ?? eventId;

  const { data: event } = useGetEvent(currentEventId);
  const { data: allTags } = useGetTags({
    eventId: currentEventId,
    type: "people",
  });

  const { mutate: createMutate, isPending: isCreating } =
    useCreateSpecialInvitation();
  const { mutate: updateMutate, isPending: isUpdating } =
    useUpdateSpecialInvitation(data?.id);

  if (!tags) {
    tags = allTags;
  }

  const tagsOptions = (tags ?? []).map((tag) => ({
    value: String(tag.id),
    label: `${tag.title} - (${tag.usageCount} users)`,
  }));

  const mainLanguage = event?.language || "en";
  const otherLanguages = event?.secondaryLanguages || [];

  const getDefaultValues = () => ({
    titleI18n: isEditMode
      ? mergeI18nWithDefaults(data?.titleI18n, mainLanguage, otherLanguages)
      : makeI18nDefaultValue(mainLanguage, otherLanguages),
    isVisible: String(data?.isVisible ?? false),
    eventId: currentEventId,
    targetedTagIds: (data?.targetedTagIds ?? []).map(String),
    image: data?.image ?? "",
  });

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { isDirty, dirtyFields },
  } = useForm({
    defaultValues: getDefaultValues(),
    resolver: resolver(web_specialInvitationSchema),
  });

  const onSubmit = (formData) => {
    const data = { ...formData };
    data.isVisible = data.isVisible === "true";

    // No tags selected targets all ticket holders
    data.targetedTagIds = data.targetedTagIds?.length
      ? data.targetedTagIds.map(Number)
      : null;

    if (isEditMode) {
      const dirtyData = extractDirtyData(data, dirtyFields);
      updateMutate(dirtyData, {
        onSuccess: () => reset({ ...formData, ...dirtyData }),
      });
    } else {
      createMutate(data, {
        onSuccess: () => {
          reset(getDefaultValues());
        },
      });
    }
  };

  const image = watch("image");

  useEffect(() => {
    if (!image) setValue("isVisible", "false", { shouldDirty: true });
  }, [image, setValue]);

  // Set eventId value to currentEventId If user changes current event, we need
  // to update eventId value in the form.
  useEffect(() => {
    setValue("eventId", currentEventId);
  }, [currentEventId, setValue]);

  // Reset form with proper i18n defaults when event languages are loaded
  useEffect(() => {
    if (event && !isEditMode) {
      reset(getDefaultValues());
    }
  }, [event?.language, event?.secondaryLanguages]);

  const localizedInputFields = [
    {
      nameBase: "titleI18n",
      label: "Title",
      component: TextInput,
      props: { mt: "sm", withAsterisk: true, placeholder: "Your title" },
    },
  ];

  return (
    <Container size={500}>
      <Paper withBorder shadow="md" p={16} mt={16} radius="md">
        <form onSubmit={handleSubmit(onSubmit)}>
          <LocalizedInput
            control={control}
            fields={localizedInputFields}
            mainLanguage={mainLanguage}
            otherLanguages={otherLanguages}
          />

          <ImageUpload
            control={control}
            name="image"
            label="Image"
            defaultImage={data?.image}
            description={
              !isEditMode
                ? "Upload an image for the special announcement"
                : undefined
            }
            mt="sm"
            resize
            aspectRatio={9 / 16}
          />

          <MultiSelect
            label="Targeted announcement"
            description="Leave empty to target all ticket holders"
            placeholder="All ticket holders"
            mt="sm"
            name="targetedTagIds"
            control={control}
            data={tagsOptions}
            searchable
            clearable
          />

          <Select
            label="Visibility"
            placeholder="Select visibility"
            mt="sm"
            name="isVisible"
            control={control}
            description={!image ? "Add an image to make it visible" : undefined}
            data={[
              { value: "true", label: "Visible", disabled: !image },
              { value: "false", label: isEditMode ? "Not Visible" : "Hidden" },
            ]}
          />

          <Button
            type="submit"
            mt="sm"
            loading={isEditMode ? isUpdating : isCreating}
            disabled={isEditMode && !isDirty}
          >
            {isEditMode ? "Edit" : "Create invitation"}
          </Button>
        </form>
      </Paper>
    </Container>
  );
}

export default SpecialInvitationForm;
